import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Container, Toolbar, useMediaQuery } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import useMenuItems from 'menu-items';
import useConfig from 'hooks/useConfig';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import UpgradePlanDrawer from 'sections/account-settings/subscriptions/billing/UpgradePlanDrawer';
import { useUpgradeDrawer } from '../../contexts/UpgradeDrawerContext';
import useAuth from '../../hooks/useAuth';
import { useBillingClient } from '../../client/client-hook';

// types
import { RootStateProps } from 'types/root';
import { openDrawer } from 'store/reducers/menu';
import { sizeByBreakpoint } from '../../utils/helper';

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('xl'));
  const navigation = useMenuItems();
  const { user } = useAuth();
  const { isOpen: isUpgradeDrawerOpen, closeUpgradeDrawer, preSelectedPlan } = useUpgradeDrawer();
  const getBillingClient = useBillingClient();

  const { container, miniDrawer } = useConfig();
  const dispatch = useDispatch();

  const menu = useSelector((state: RootStateProps) => state.menu);
  const { drawerOpen } = menu;

  // State for upgrade drawer data
  const [subscriptionsList, setSubscriptionsList] = useState<any[]>([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [paymentCards, setPaymentCards] = useState<any[]>([]);

  // Fetch billing data when upgrade drawer opens
  useEffect(() => {
    if (isUpgradeDrawerOpen && user?.tcId) {
      getBillingClient().then(async (client) => {
        try {
          const [overview, subList, cardsResp] = await Promise.all([
            client.billingOverview({ userId: user.tcId || '' }),
            client.listSubscriptions({ userId: user.tcId || '', statusList: ['active'] }),
            client.listCards({ userId: user.tcId || '' })
          ]);
          setCreditBalance((overview as any)?.creditBalance || 0);
          setSubscriptionsList((subList as any).subscriptionsList || []);
          setPaymentCards((cardsResp as any).cardsList || []);
        } catch (error) {
          console.error('Failed to fetch billing data:', error);
        }
      });
    }
  }, [isUpgradeDrawerOpen, user?.tcId, getBillingClient]);

  // drawer toggler
  const [open, setOpen] = useState(!miniDrawer || drawerOpen);
  const handleDrawerToggle = () => {
    const newState = !open;
    setOpen(newState);
    dispatch(openDrawer({ drawerOpen: newState }));
    localStorage.setItem('sidebarOpen', String(newState));
  };

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      setOpen(!matchDownLG);
      dispatch(openDrawer({ drawerOpen: !matchDownLG }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownLG]);

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Header open={open} handleDrawerToggle={handleDrawerToggle} />
      <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />
      <Box
        component="main"
        sx={{
          width: 'calc(100% - 288px)',
          flexGrow: 1,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3, md: 10 },
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Toolbar sx={{ minHeight: sizeByBreakpoint(0, 0, 0, 0, 56, 64) }} />
        {container && (
          <Container
            maxWidth="lg"
            sx={{
              px: { xs: 0, sm: 2 },
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Breadcrumbs navigation={navigation} title titleBottom card={false} divider={false} />
            <Outlet />
            <Footer />
          </Container>
        )}
        {!container && (
          <Box sx={{ position: 'relative', minHeight: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
            <Breadcrumbs navigation={navigation} title titleBottom card={false} divider={false} />
            <Outlet />
            <Footer />
          </Box>
        )}
      </Box>
      
      {/* Global Upgrade Plan Drawer */}
      <UpgradePlanDrawer
        isOpen={isUpgradeDrawerOpen}
        onClose={closeUpgradeDrawer}
        onSuccess={() => {
          closeUpgradeDrawer();
        }}
        onCardsUpdated={(cards) => {
          setPaymentCards(cards);
        }}
        subscriptions={subscriptionsList}
        creditBalance={creditBalance}
        paymentCards={paymentCards}
        preSelectedPlan={preSelectedPlan}
      />
    </Box>
  );
};

export default MainLayout;
